import { ArrowUpRight, ChevronRight, Crown, Ellipsis, Globe } from 'lucide-react';
import { useLocale } from '../../../lib/locale-context';
import { useTranslation } from '../../../lib/i18n';
import type { PresenceAction, PresenceStrip, StripAction, StripCell } from '../../../lib/presence-checklist';
import { Badge, Card, IconButton, Typography } from '../ui';
import { PresenceActionButton, usePresenceCopy } from './PresenceChecklist';
import { formatCount, TrendChip, useTrendWords } from './SiteFigures';
import { Sparkline } from './Sparkline';
import styles from './SiteStrip.module.css';

// The Space header's site strip (spec 2026-10-25 §4): ONE card, three bands —
//   1. identity: the site's picture (or a globe), its name and address, ⋯ = Manage (opens the Online presence card),
//      then two equal pill buttons: View site ↗ and the site's Edit (`stripEditAction`: Edit site / Review site);
//   2. Visits: the whole tile opens the in-app Analytics page (never Wix) — the number, its trend chip and a 30-day
//      sparkline; a read that failed shows "—" and still opens the page;
//   3. small tiles for Plan · Domain · Business email · On Wix, each a value and ONE chip action.
// Desktop lays the three bands side by side in one row. Exactly one accent, as before: the checklist's next step, in
// the tile that owns it (filled), or after the identity buttons when no tile owns it. Every decision is
// `presenceStrip` in lib/presence-checklist.ts (unit-tested, mirrored by both phones); this file only draws it.

const TONE_CLASS: Record<StripCell['tone'], string> = {
  warning: styles.toneWarning,
  success: styles.toneSuccess,
  danger: styles.toneDanger,
  neutral: '',
};

/** The em dash a Visits read that failed shows: a figure, not a word, so it is not a catalog string. */
const UNKNOWN = '—';

function useCellText() {
  const copy = usePresenceCopy();
  const { locale } = useLocale();
  return (cell: StripCell): string =>
    'unknown' in cell.value ? UNKNOWN : 'count' in cell.value ? formatCount(cell.value.count, locale) : copy(cell.value);
}

function Identity({ cell, trailing, onAct, onManage }: { cell: StripCell; trailing: StripAction | null; onAct: (a: PresenceAction) => void; onManage?: () => void }) {
  const { t } = useTranslation();
  const text = useCellText()(cell);
  const buttons = [cell.action, cell.edit].filter((a): a is StripAction => a !== null);
  return (
    <div className={styles.identity} data-testid="siteStripCell-site">
      <div className={styles.identityTop}>
        <span className={styles.thumb}>
          {cell.thumbnailUrl ? <img src={cell.thumbnailUrl} alt="" className={styles.thumbImg} loading="lazy" /> : <Globe size={20} aria-hidden />}
        </span>
        <span className={styles.identityText}>
          <Typography variant="p1" color="strong" component="span" noWrap className={styles.siteName}>
            {text}
          </Typography>
          {cell.sub && (
            <Typography variant="p3" color="secondary" component="span" noWrap>
              {cell.sub}
            </Typography>
          )}
        </span>
        {onManage && (
          <IconButton size="small" variant="text" aria-label={t('presence.action.manage')} title={t('presence.action.manage')} data-testid="siteStripManage" onClick={onManage}>
            <Ellipsis size={18} aria-hidden />
          </IconButton>
        )}
      </div>
      {(buttons.length > 0 || trailing) && (
        <div className={styles.pills}>
          {buttons.map((a, i) => (
            <PresenceActionButton
              key={a.bi}
              action={a}
              onAct={onAct}
              variant="outlined"
              size="small"
              className={styles.pill}
              testId={i === 0 && cell.action ? 'siteStripLink-site' : 'siteStripEdit-site'}
              endIcon={a === cell.action && a.target.kind === 'url' ? <ArrowUpRight size={14} aria-hidden className="dir-flip" /> : undefined}
            />
          ))}
          {trailing && <PresenceActionButton action={trailing} onAct={onAct} color="accent" size="small" testId="siteStripPrimary" className={styles.pillWide} />}
        </div>
      )}
    </div>
  );
}

function VisitsTile({ cell, onAct }: { cell: StripCell; onAct: (a: PresenceAction) => void }) {
  const { t } = useTranslation();
  const text = useCellText()(cell);
  const words = useTrendWords();
  const label = t(cell.label.key);
  const action = cell.action;
  return (
    <Card
      component="button"
      variant="stone"
      size="sm"
      interactive
      className={styles.visits}
      data-testid="siteStripCell-visits"
      aria-label={[label, text, words(cell.trend)].filter(Boolean).join(', ')}
      onClick={() => action && onAct(action)}
    >
      <span className={styles.visitsHead}>
        <Typography variant="p3" color="secondary" component="span" className={styles.label}>
          {label}
        </Typography>
        <ChevronRight size={16} aria-hidden className={`${styles.chevron} dir-flip`} />
      </span>
      <span className={styles.visitsBody}>
        <span className={styles.visitsFigure}>
          <span className={styles.visitsNumber}>{text}</span>
          {cell.trend && <TrendChip trend={cell.trend} />}
        </span>
        {cell.daily.length > 0 && <Sparkline values={cell.daily} width={112} height={36} className={styles.spark} />}
      </span>
    </Card>
  );
}

function SmallTile({ cell, onAct }: { cell: StripCell; onAct: (a: PresenceAction) => void }) {
  const { t } = useTranslation();
  const text = useCellText()(cell);
  return (
    <Card variant="stone" size="sm" className={styles.tile} data-testid={`siteStripCell-${cell.kind}`}>
      <Typography variant="p3" color="secondary" component="span" className={styles.label}>
        {t(cell.label.key)}
      </Typography>
      {cell.badge ? (
        <Badge variant="done" size="small" startIcon={<Crown size={14} aria-hidden />} label={text} className={styles.badge} />
      ) : (
        <Typography variant="p2" color="strong" component="span" noWrap className={`${styles.value} ${TONE_CLASS[cell.tone]}`}>
          {text}
        </Typography>
      )}
      {cell.action && (
        <PresenceActionButton
          action={cell.action}
          onAct={onAct}
          variant={cell.action.primary ? 'contained' : 'outlined'}
          color={cell.action.primary ? 'accent' : 'standard'}
          size="tiny"
          className={styles.chipAction}
          testId={cell.action.primary ? 'siteStripPrimary' : `siteStripLink-${cell.kind}`}
        />
      )}
    </Card>
  );
}

/** `onManage` absent (the bindings read failed, so there is no card to open) draws no ⋯. */
export function SiteStrip({ strip, onAct, onManage }: { strip: PresenceStrip; onAct: (action: PresenceAction) => void; onManage?: () => void }) {
  const site = strip.cells.find((c) => c.kind === 'site');
  const visits = strip.cells.find((c) => c.kind === 'visits');
  const tiles = strip.cells.filter((c) => c.kind !== 'site' && c.kind !== 'visits');
  return (
    <Card component="section" variant="outline" size="sm" className={styles.strip} data-testid="siteStrip">
      {site && <Identity cell={site} trailing={strip.trailingPrimary} onAct={onAct} onManage={onManage} />}
      {visits && <VisitsTile cell={visits} onAct={onAct} />}
      {tiles.length > 0 && (
        <div className={styles.tiles} data-count={tiles.length}>
          {tiles.map((cell) => (
            <SmallTile key={cell.kind} cell={cell} onAct={onAct} />
          ))}
        </div>
      )}
    </Card>
  );
}
